import { useState } from "react";

import { useRequestAccessFromAdminMutation } from "app/pages/sales-intelligence/query/mutations/useRequestAccessFromAdminMutation";
import localStorageService from "app/pages/sales-intelligence/services/localStorageService";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import type { RequestAdminFeature } from "app/pages/sales-intelligence/types/requestAdminFeature";

const REQUEST_ADMIN_TTL_MS = 24 * 60 * 60 * 1000;

interface IStoredRequestState {
    sentAt: number;
}

interface IUseRequestAdminAccessResult {
    isSent: boolean;
    requestAccess: () => void;
}

const readPersistedIsSent = (storageKey: string): boolean => {
    const stored = localStorageService.getState<IStoredRequestState>(storageKey);

    if (!stored?.sentAt) {
        return false;
    }

    if (Date.now() - stored.sentAt >= REQUEST_ADMIN_TTL_MS) {
        localStorage.removeItem(storageKey);
        return false;
    }

    return true;
};

export const useRequestAdminAccess = (
    storageKeyPrefix: string,
    featureName?: RequestAdminFeature,
): IUseRequestAdminAccessResult => {
    const storageKey = `${storageKeyPrefix}${featureName}`;
    const [isSent, setIsSent] = useState(() => readPersistedIsSent(storageKey));
    const { mutate: requestAccessFromAdmin } = useRequestAccessFromAdminMutation();

    const requestAccess = () => {
        if (!featureName) {
            return;
        }

        integrationTrackingService.trackRequestAccess(featureName);

        requestAccessFromAdmin(featureName, {
            onSuccess: () => {
                localStorageService.setState(storageKey, { sentAt: Date.now() } satisfies IStoredRequestState);
                setIsSent(true);
            },
        });
    };

    return { isSent, requestAccess };
};
